import authService from './authService';
import { ensureOk, readData, readList, toQuery } from './apiResponse';
import type { RecurringTargetType, RecurringTransaction, RecurringTransactionInput } from '../types/finance';

const withSkips = (r: RecurringTransaction): RecurringTransaction => ({ ...r, skips: r?.skips ?? [] });

/**
 * Finanças → Recorrências: lançamento mensal estimado em conta ou cartão, gerado pelo job numa
 * janela de 12 meses (e na hora ao criar/editar). Linha gerada editada pelo usuário deixa de ser
 * atualizada pela recorrência. Competências em AAAAMM.
 */
class RecurringService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /** GET /recurring-transactions?include_inactive=1&target_type= */
  async getAll(includeInactive = false, targetType?: RecurringTargetType | ''): Promise<RecurringTransaction[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/recurring-transactions${toQuery({ include_inactive: includeInactive ? 1 : undefined, target_type: targetType || undefined })}`,
    );
    return (await readList<RecurringTransaction>(response, 'Falha ao listar as recorrências')).map(withSkips);
  }

  /** GET /recurring-transactions/:id */
  async getById(id: number): Promise<RecurringTransaction> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/recurring-transactions/${id}`);
    return withSkips(await readData<RecurringTransaction>(response, 'Falha ao carregar a recorrência'));
  }

  /** POST /recurring-transactions — gera a janela na hora. */
  async create(input: RecurringTransactionInput): Promise<RecurringTransaction> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/recurring-transactions', input);
    return withSkips(await readData<RecurringTransaction>(response, 'Falha ao criar a recorrência'));
  }

  /** PUT /recurring-transactions/:id — regenera a janela (só linhas ainda do gerador). */
  async update(id: number, input: RecurringTransactionInput): Promise<RecurringTransaction> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/recurring-transactions/${id}`, input);
    return withSkips(await readData<RecurringTransaction>(response, 'Falha ao atualizar a recorrência'));
  }

  /** DELETE /recurring-transactions/:id — inativa (exclui as linhas futuras ainda não editadas). */
  async deactivate(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/recurring-transactions/${id}`);
    await ensureOk(response, 'Falha ao inativar a recorrência');
  }

  /** POST /recurring-transactions/:id/skips { competence } — 409 se a linha gerada já foi editada. */
  async addSkip(id: number, competence: number): Promise<RecurringTransaction> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/recurring-transactions/${id}/skips`, { competence });
    return withSkips(await readData<RecurringTransaction>(response, 'Falha ao pular a competência'));
  }

  /** DELETE /recurring-transactions/:id/skips/:competence — volta a gerar. */
  async removeSkip(id: number, competence: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/recurring-transactions/${id}/skips/${competence}`);
    await ensureOk(response, 'Falha ao reativar a competência');
  }
}

export const recurringService = new RecurringService();
export default recurringService;
